import { useEffect, useMemo, useState } from 'react';
import { UserCircle } from 'lucide-react';
import DataTable from '../../components/ui/DataTable.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import api from '../../services/api.js';
import { toastError } from '../../hooks/useToast.js';
import { useAuthStore } from '../../store/authStore.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function UsuariosAdminPage() {
  const currentUser = useAuthStore((s) => s.user);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/admin/users', {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearch || undefined,
        },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const columns = useMemo(
    () => [
      {
        key: 'nombre',
        label: 'Nombre',
        render: (row) => (
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0d2b7f]/10 text-[#0d2b7f]">
              <UserCircle className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            </span>
            <span className="font-medium text-slate-800">{row.nombre || '—'}</span>
            {Number(currentUser?.id) === Number(row.id) ? (
              <span className="rounded-full bg-[#1acb12]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0d2b7f] ring-1 ring-[#1acb12]/35">
                Tú
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'email',
        label: 'Correo',
        render: (row) => <span className="text-slate-700">{row.email}</span>,
      },
      {
        key: 'created_at',
        label: 'Registrado',
        render: (row) => formatDateTime(row.created_at),
      },
    ],
    [currentUser?.id]
  );

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Usuarios del sistema</h1>
        <p className="mt-3 max-w-2xl text-slate-600">
          Consulta las cuentas con acceso al panel administrativo. Este módulo es solo de lectura; cada usuario
          puede actualizar su propio perfil desde el nombre en la barra superior.
        </p>
      </div>

      <PremiumCard variant="light" className="!p-5 sm:!p-6">
        <DataTable
          columns={columns}
          rows={items}
          loading={loading}
          search={search}
          onSearchChange={setSearch}
          page={page}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          emptyText="No hay usuarios registrados"
        />
      </PremiumCard>
    </div>
  );
}
